import { useEffect, useRef } from 'react';
import { useActiveJob } from '../use-active-job';
import { useTesterPresence } from '../use-tester-presence';
import { useTesterQueue } from '../use-tester-queue';
import { useActivityAudio } from './audio-context';

/**
 * Headless observer that maps React Query transitions to audio cues (#3972).
 *
 * Renders nothing. Subscribes to the same three queries `<TesterView>` uses
 * (`useTesterQueue`, `useActiveJob`, `useTesterPresence`) — React Query
 * de-duplicates by query key, so we share TanStack's cache instead of
 * issuing a parallel fetch.
 *
 * First-snapshot guard: each ref is initialized to `null`, and we treat the
 * first poll result as the baseline (record IDs, fire nothing). This is the
 * mitigation for "don't chime through the entire current queue when the
 * Activity hydrates" called out in AC #3 of the issue.
 *
 * One cue per refetch per kind: if three jobs land in `available` between
 * polls, we play `testAvailable` once. Same for the presence buckets. This
 * prevents cacophony when a backend batch lands.
 */
export function ActivityAudioObserver({ accessToken }: { readonly accessToken: string }) {
  const { play } = useActivityAudio();
  const queueQuery = useTesterQueue(accessToken);
  const activeJobQuery = useActiveJob(accessToken);
  const presenceQuery = useTesterPresence(accessToken);

  // `null` here means "no snapshot recorded yet" — distinct from "empty set".
  const queueAvailableIdsRef = useRef<ReadonlySet<string> | null>(null);
  const queueInProgressIdsRef = useRef<ReadonlySet<string> | null>(null);
  const activeJobIdRef = useRef<string | null | undefined>(undefined);
  const presenceOnlineIdsRef = useRef<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (!queueQuery.data) return;
    const nextAvailable = new Set(queueQuery.data.available.map((j) => j.id));
    const nextInProgress = new Set(queueQuery.data.inProgress.map((j) => j.id));

    const prevAvailable = queueAvailableIdsRef.current;
    const prevInProgress = queueInProgressIdsRef.current;

    if (prevAvailable !== null && prevInProgress !== null) {
      const newlyAvailable = anyMissing(nextAvailable, prevAvailable, prevInProgress);
      if (newlyAvailable) play('testAvailable');

      const newlyClaimed = anyClaimed(nextInProgress, prevAvailable, prevInProgress);
      if (newlyClaimed) play('testClaimed');
    }

    queueAvailableIdsRef.current = nextAvailable;
    queueInProgressIdsRef.current = nextInProgress;
  }, [queueQuery.data, play]);

  useEffect(() => {
    if (!activeJobQuery.data) return;
    const activeJob = activeJobQuery.data.activeJob;
    // `ActiveJobInfo | null` is the contract; branch exhaustively on the
    // discriminator. The ref's `undefined` slot is reserved for "no snapshot
    // yet" and is set below — distinct from the null "no active job" value.
    const nextId: string | null = activeJob === null ? null : activeJob.id;
    const prevId = activeJobIdRef.current;

    if (prevId !== undefined) {
      const cleared = prevId !== null && nextId === null;
      const switched = prevId !== null && nextId !== null && prevId !== nextId;
      if (cleared || switched) play('testCompleted');
    }

    activeJobIdRef.current = nextId;
  }, [activeJobQuery.data, play]);

  useEffect(() => {
    if (!presenceQuery.data) return;
    const nextOnline = new Set(
      presenceQuery.data.testers
        .filter((t) => t.availability === 'online')
        .map((t) => t.id),
    );
    const prevOnline = presenceOnlineIdsRef.current;

    if (prevOnline !== null) {
      const cameOnline = anyAdded(nextOnline, prevOnline);
      if (cameOnline) play('testerOnline');

      const wentOffline = anyAdded(prevOnline, nextOnline);
      if (wentOffline) play('testerOffline');
    }

    presenceOnlineIdsRef.current = nextOnline;
  }, [presenceQuery.data, play]);

  return null;
}

function anyAdded(next: ReadonlySet<string>, prev: ReadonlySet<string>): boolean {
  for (const id of next) {
    if (!prev.has(id)) return true;
  }
  return false;
}

/**
 * True if `next` contains any id absent from BOTH `prev` and `prevOther`. Used
 * for "newly posted available job" — a job moving from `inProgress` back into
 * `available` (release) should not chime as if it were brand new.
 */
function anyMissing(
  next: ReadonlySet<string>,
  prev: ReadonlySet<string>,
  prevOther: ReadonlySet<string>,
): boolean {
  for (const id of next) {
    if (!prev.has(id) && !prevOther.has(id)) return true;
  }
  return false;
}

/**
 * True if any id in `nextInProgress` was previously in `prevAvailable` and
 * not yet in `prevInProgress` — i.e. an actual available→inProgress claim
 * transition between polls.
 */
function anyClaimed(
  nextInProgress: ReadonlySet<string>,
  prevAvailable: ReadonlySet<string>,
  prevInProgress: ReadonlySet<string>,
): boolean {
  for (const id of nextInProgress) {
    if (prevAvailable.has(id) && !prevInProgress.has(id)) return true;
  }
  return false;
}
